/**
 * @file src/core/viewPreset.js
 * CÔNG THỨC HIỂN THỊ CHUẨN — một nguồn sự thật duy nhất cho cách render model.
 *
 * Vì sao có file này: bản dựng dùng để soi model là GLB.check (clone của
 * gltf-viewer.donmccurdy.com). Nếu bài giảng render bằng bộ số khác thì thứ giáo viên
 * duyệt trong GLB.check KHÔNG phải thứ học sinh nhìn thấy — mọi lần chỉnh sáng đều
 * thành đoán mò. Ở đây khai đúng bộ số của gltf-viewer/GLB.check; Engine đọc từ đây,
 * tiết KHÔNG tự dựng đèn nữa.
 *
 * Sửa số ở đây thì phải sửa cả GLB.check/viewer.js cho khớp, và ngược lại.
 *
 * Bộ số (gltf-viewer, Viewer.addLights + renderer setup):
 *   tone mapping ACESFilmic · exposure 1.0 · outputColorSpace sRGB
 *   scene.environment = PMREM(RoomEnvironment, 0.04)   ← "neutral"
 *   AmbientLight  0xffffff × 0.3
 *   DirectionalLight 0xffffff × (0.8 × π ≈ 2.513) tại (0.5, 0, 0.866), GẮN VÀO CAMERA
 *   envMapIntensity 1.0 (mặc định của three, không hạ)
 *   anisotropy = tối đa GPU cho phép
 *
 * Vì sao đèn gắn vào camera: mặt đang hướng về người xem luôn có mặt sáng/mặt tối, xoay
 * tới góc nào cũng không rơi vào vùng đen. Chỉ có IBL thì khối lồi mất hết tương phản.
 * Vì sao 2.513 chứ không phải 0.8: three r155+ bỏ useLegacyLights, đèn tính theo đơn vị
 * vật lý — 0.8 của bản cũ tương đương 0.8 × π.
 */

export const VIEW_PRESET = Object.freeze({
  /** Nhân với π vì three r155+ dùng đơn vị vật lý. */
  exposure: 1.0,
  envIntensity: 1.0,
  ambient: Object.freeze({ color: 0xffffff, intensity: 0.3 }),
  key: Object.freeze({
    color: 0xffffff,
    intensity: 0.8 * Math.PI,
    /**
     * Toạ độ trong KHÔNG GIAN CAMERA (đèn là con của camera).
     * @type {[number, number, number]}
     */
    position: /** @type {[number, number, number]} */ (Object.freeze([0.5, 0, 0.866])),
  }),
  /** Độ nhám dùng khi sinh PMREM từ RoomEnvironment. */
  envBlur: 0.04,

  /**
   * ĐỔ BÓNG. Đây là chỗ DUY NHẤT lệch khỏi gltf-viewer gốc (bản đó không có bóng) — nên
   * GLB.check cũng phải bật cùng bộ số này, không thì ảnh duyệt lại khác ảnh học sinh.
   *
   * Vì sao phải có MỘT ĐÈN THỨ HAI: key light của bộ chuẩn gắn vào camera, tức rọi gần
   * như dọc theo hướng nhìn. Đèn kiểu đó về nguyên tắc KHÔNG tạo được bóng nhìn thấy —
   * bóng đổ đúng vào phần vật thể che nó. Muốn thấy bóng thì phải có nguồn sáng lệch
   * khỏi trục nhìn và ĐỨNG YÊN trong không gian thế giới, như mặt trời thật.
   *
   * Tổng năng lượng KHÔNG đổi: khi bật bóng, 0.8π được chia cho key (0.45) và mặt trời
   * (0.55). Nhờ vậy độ sáng chung giữ nguyên, chỉ đổi cách phân bố sáng/tối.
   * Khi TẮT bóng thì key nhận lại đủ 0.8π ⇒ trùng khít gltf-viewer như trước.
   */
  shadow: Object.freeze({
    enabled: true,
    /** Cỡ shadow map. tier "low" hạ xuống 1024 trong boot.js. */
    mapSize: 2048,

    // BẢNG MÀU "NGOÀI TRỜI CÓ NẮNG" — dùng khi bật bóng.
    //
    // Bộ studio của gltf-viewer (ambient trắng + IBL phòng chụp) cho ảnh BẠC và BẸT: cát
    // ra gần như trắng, bóng chỉ làm tối 3% (9/255) vì ánh sáng đến từ mọi phía nên lấp
    // đầy vùng bóng. Muốn giống ảnh tham chiếu của Mozaik thì phải dựng đúng cách ngoài
    // trời chiếu sáng:
    //   · MẶT TRỜI ấm, áp đảo    → mặt hướng nắng vàng ấm, mặt khuất tối rõ
    //   · HEMISPHERE thay ambient → trời xanh hắt xuống vùng bóng (bóng ngả LẠNH), cát
    //     hắt ngược lên (bounce ẤM). Chính cặp ấm–lạnh này làm ảnh "lên màu", chứ không
    //     phải tăng bão hoà.
    //   · IBL chỉ còn 0.15       → giữ chút phản xạ môi trường, không lấp bóng
    //   · Tone mapping NEUTRAL   → ACES kéo màu ấm về xám và bóp bão hoà; Neutral (chuẩn
    //     Khronos PBR) giữ đúng màu cát ochre. Đây là lý do ảnh trước nhìn bạc.
    // Đo trên model Giza: bóng phủ 54% khung, trung bình tối 30/255 (bộ studio: 0,1% và
    // 4/255). Mean pixel 184 — đúng vùng midtone của ảnh Mozaik, không còn cháy sáng.
    sun: Object.freeze({ color: 0xfff0d8, intensity: 6.0 }),
    hemi: Object.freeze({ sky: 0xbcd7ff, ground: 0xc8a878, intensity: 0.45 }),
    /** Đèn gắn camera chỉ còn là fill rất nhẹ, chống mảng tối chết. */
    keyIntensity: 0.3,
    envIntensity: 0.15,
    /** Tên hằng tone mapping của three (THREE[`${x}ToneMapping`]). */
    toneMapping: 'Neutral',

    /**
     * Hướng TỚI mặt trời trong KHÔNG GIAN THẾ GIỚI (đã đơn vị hoá): cao 32°, phương vi 160°.
     * · 32° (không phải 45°+): bóng dài ~1,6 lần chiều cao vật nên đọc được ở khung xa;
     *   mặt trời đứng bóng thì bóng thu về gần như biến mất.
     * · 160°: lệch 70–110° so với phương vi của cả hai điểm nhìn trong model (bước 1 ở
     *   −90°, bước 2 ở ~53°). Đặt mặt trời cùng phía với camera là bóng đổ ra SAU vật,
     *   bị chính vật che mất — đo được chỉ còn 0,1% khung có bóng.
     * @type {[number, number, number]}
     */
    direction: /** @type {[number, number, number]} */ (Object.freeze([-0.797, 0.53, 0.29])),
    /**
     * Chống shadow acne, tính bằng SỐ TEXEL của shadow map (fitShadows quy ra đơn vị
     * model). Phải theo texel chứ không theo đơn vị model: hạ mapSize xuống 1024 thì mỗi
     * texel phủ gấp đôi diện tích, cần gấp đôi bias.
     * Đo trên model Giza: 0 texel ⇒ acne phủ 23% khung; 1.5 texel ⇒ NUỐT LUÔN bóng thật
     * (chỉ còn 0,2% khung); 0.5 texel là điểm cân bằng.
     */
    normalBiasTexels: 0.5,
    bias: 0,
    /**
     * Độ mềm viền bóng (số texel PCF lấy mẫu rộng ra). KHÔNG dùng PCFSoftShadowMap: từ
     * three r184 loại đó đã bị khai tử, renderer tự ép về PCFShadowMap ngay frame đầu.
     */
    softness: 2.5,
    /**
     * BÁM THEO CAMERA. Bật thì hướng mặt trời không còn cố định theo `direction` nữa mà
     * suy ra từ trục camera→target: xoay model tới đâu, ánh sáng đi theo tới đó.
     * `direction` ở trên vẫn dùng làm hướng khởi đầu và làm hướng dự phòng khi tắt.
     *
     * azimuthOffsetDeg — LỆCH NGANG so với trục camera→target. KHÔNG được để 0: đặt mặt
     *   trời đúng sau lưng người xem thì bóng đổ ra sau vật, bị chính vật che kín. Đo
     *   được trên model Giza ở bản trước: cùng phương vi với camera ⇒ chỉ 0,1% khung còn
     *   thấy bóng. 45° là góc key light kinh điển — bóng đổ chếch sang một bên, đọc được
     *   khối của vật mà vẫn theo hướng nhìn.
     * elevationDeg — độ cao mặt trời, GIỮ CỐ ĐỊNH (32°, đúng như `direction` cũ) chứ
     *   không lấy theo độ cao của camera: camera hạ xuống sát chân trời là mặt trời cũng
     *   tụt theo, bóng dài vô tận và cảnh tối sầm. null = lấy theo camera.
     * updateDeg — đổi hướng quá ngần này (độ) mới vẽ lại shadow map. Vẽ lại mỗi frame
     *   trong lúc xoay là tốn nhất, mà mắt không phân biệt nổi dưới vài độ. Tăng số này
     *   nếu model nặng và thấy tụt khung hình lúc xoay.
     */
    follow: Object.freeze({
      enabled: true,
      azimuthOffsetDeg: 45,
      elevationDeg: 32,
      updateDeg: 3,
    }),
    /** Khoảng cách đặt mặt trời = bán kính vùng đổ bóng × hệ số này. */
    distanceFactor: 2.5,
    /**
     * Mesh có bán kính > tỉ lệ này so với cả model thì KHÔNG đổ bóng. Chặn đúng thứ phải
     * chặn: vòm trời bao quanh cảnh — để nó đổ bóng là cả cảnh chìm trong tối.
     */
    casterMaxRatio: 0.4,
    /**
     * Mesh BAO QUANH cảnh (bán kính ≥ tỉ lệ này so với cả model VÀ hộp bao chứa tâm model)
     * được coi là VÒM TRỜI và render KHÔNG CHIẾU SÁNG.
     *
     * Vì sao: panorama trên vòm trời là ảnh đã "nướng" sẵn ánh sáng — chiếu sáng nó lần
     * nữa là sai về nguyên tắc. Ở bộ studio (sáng đều mọi phía) lỗi này ẩn đi vì vòm sáng
     * đều; sang bảng màu ngoài trời thì nửa khuất nắng của vòm xám đen ngay, nhìn như
     * trời sắp bão.
     */
    skyDomeRatio: 0.8,
  }),

  /**
   * ĐÈN DO MODEL MANG THEO (`KHR_lights_punctual`).
   *
   * Bộ số này là CỦA NỀN TẢNG, giống nhau cho MỌI model — không phải chỉnh riêng theo
   * từng bài. Model nào sáng/tối lệch thì sửa ở CHÍNH MODEL (Sun Strength trong Blender),
   * không thêm khoá đè trong config của tiết. Mỗi bài một bộ số riêng là mất luôn cái
   * đích "đổi model xong chạy được ngay".
   */
  modelLights: Object.freeze({
    /**
     * 'auto'   — model có đèn thì DÙNG đèn của model, tắt đèn hướng của preset (mặc định)
     * 'preset' — luôn dùng bộ preset
     * 'model'  — luôn tắt đèn hướng của preset
     */
    mode: /** @type {'auto'|'preset'|'model'} */ ('auto'),
    /**
     * 'blender' — quy cường độ về thang của Blender. Bộ xuất glTF nhân với **683** (hệ số
     *   W → lumen): Sun Strength 4.0 trong Blender ra 2732 lux trong file. Chia ngược lại
     *   ra đúng 4.0, cùng thang với mặt trời 6.0 của preset.
     * 'gltf'    — giữ nguyên số trong file (model xuất từ công cụ khác Blender).
     */
    unit: /** @type {'blender'|'gltf'} */ ('blender'),
    /**
     * Hệ số nhân thêm sau quy đổi. GIỮ 1 — hiệu chỉnh sáng/tối là việc của MODEL.
     * Hợp đồng với bên 3D: đặt **Sun Strength = 4.0** trong Blender (xem
     * HUONG-DAN-DUNG-MODEL-BLENDER.md phần 2). Đo trên model Giza: Sun 4 cho độ sáng
     * trung bình khung hình 172, bản duyệt bằng bộ preset là 176.
     */
    scale: 1,
    /**
     * Giữ đèn NỀN (hemisphere) của preset khi dùng đèn model. glTF chở được đèn punctual
     * nhưng KHÔNG chở được World của Blender, nên model luôn thiếu đúng phần ánh sáng
     * nền. Tắt nốt thì vùng khuất nắng đen kịt — đo được: tắt ⇒ 1,8% khung tối đen, giữ
     * ⇒ 1,1%. Chỉ hai đèn HƯỚNG (key, sun) của preset mới bị tắt.
     */
    keepAmbient: true,
    /**
     * Đèn nào của model được đổ bóng. 'directional' = directional + spot (mặc định);
     * 'all' gồm cả point — bóng đèn điểm là shadow map LẬP PHƯƠNG, sáu mặt, đắt gấp sáu;
     * false = không đèn nào.
     */
    castShadow: /** @type {'directional'|'all'|false} */ ('directional'),
    /** Tối đa bấy nhiêu đèn đổ bóng. Vượt quá vẫn chiếu sáng, chỉ không đổ bóng. */
    shadowMax: 2,
    /**
     * Cường độ ÁNH SÁNG MÔI TRƯỜNG khi dùng đèn của model. null = giữ nguyên của preset.
     *
     * Vì sao phải NÂNG so với preset (0.15): glTF không chở được **World của Blender**,
     * mà World chính là thứ hắt sáng từ MỌI PHÍA và lấp các mặt khuất nắng. Bên ta chỉ
     * còn hai thứ thay chỗ — hemisphere và IBL — và con số 0.15 vốn được cân cho đường
     * PRESET (mặt trời 6.0 gánh phần chính), không đủ cho đường đèn-của-model.
     *
     * Đo trên model Giza, cùng một khung hình:
     *   env 0.15 → sáng TB 175,5 · 7,4% khung tối (<80)
     *   env 0.25 → 183,0 · 4,7%
     *   env 0.30 → ~186  · ~4%      ← chọn
     *   env 0.45 → 194,2 · 2,1%
     * Bản duyệt bằng bộ preset ở cùng khung là 170,3 · 9,1%. Không mức nào gây cháy sáng
     * (0% điểm >250). Chọn 0.3: nhỉnh hơn bản duyệt một chút — đúng hướng "Blender sáng
     * hơn" — mà chưa bệt mất khối.
     *
     * NÂNG HEMISPHERE KHÔNG THAY ĐƯỢC: đo được 0.45 → 1.2 chỉ thêm ~6 đơn vị sáng và
     * gần như không giảm vùng tối, vì hemisphere chỉ hắt từ trên trời và dưới đất, còn
     * IBL hắt từ mọi hướng — đúng thứ mặt khuất nắng cần.
     */
    envIntensity: /** @type {number|null} */ (0.3),
  }),
});
